import React from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, PlayCircle, Church, HeartHandshake, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFetch } from "@/lib/api";
import { Reveal } from "@/components/blocks/Reveal";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { EmptyState } from "@/components/blocks/EmptyState";
import { ActivityCard, EventCard, SermonCard, PodcastCard } from "@/components/cards/Cards";
import { getIcon } from "@/lib/icons";

const Home = () => {
  const { data: m } = useFetch("/ministry");
  const { data: activities } = useFetch("/evangelism");
  const { data: events } = useFetch("/events");
  const { data: sermons } = useFetch("/sermons");
  const { data: podcasts } = useFetch("/podcasts");
  const { data: church } = useFetch("/church");
  const { data: stats } = useFetch("/statistics");

  const img = m?.images || {};

  return (
    <div>
      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden bg-navy">
        <div className="absolute inset-0">
          <img src={img.hero} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Marquee background scripture */}
        <div className="pointer-events-none absolute bottom-8 left-0 w-full overflow-hidden opacity-[0.06]">
          <div className="flex w-max animate-marquee whitespace-nowrap">
            {[0, 1].map((k) => (
              <span key={k} className="outline-text px-6 font-display text-[9rem] font-extrabold uppercase">
                Is My Word not a fire? · Luke 10:9 ·
              </span>
            ))}
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-7xl px-5 pt-24 lg:px-8">
          <Reveal>
            <span className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-4 py-2 font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
              <Flame className="h-3.5 w-3.5" /> International & Interdenominational
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-7 max-w-4xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Flames of Fire <span className="text-brand-orange">World Evangelism</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl font-display text-lg font-light leading-relaxed text-white/80 md:text-2xl">
              Setting the World on Fire,
              <br className="hidden sm:block" /> One Nation at a time.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Button
                asChild
                data-testid="hero-explore-evangelism-btn"
                className="h-13 rounded-sm bg-brand-orange px-8 py-6 font-display text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform duration-300 hover:scale-105 hover:bg-brand-orange-600"
              >
                <Link to="/evangelism">
                  Explore Our Evangelism <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-watch-sermons-btn"
                className="h-13 rounded-sm border-white/25 bg-transparent px-8 py-6 font-display text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-white hover:text-navy"
              >
                <Link to="/sermons">
                  <PlayCircle className="mr-2 h-4 w-4" /> Watch Sermons
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:items-center lg:gap-20 lg:px-8">
          <div>
            <SectionHeading overline="Who We Are" title="A ministry born of a prophetic mandate" />
            <p className="mt-6 text-base leading-relaxed text-muted-foreground md:text-lg">{m?.who_we_are}</p>
            <Button asChild variant="outline" className="mt-8 rounded-sm border-navy font-display uppercase tracking-wide text-navy hover:bg-navy hover:text-white">
              <Link to="/about" data-testid="home-about-link">Read Our Story <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <Reveal delay={0.1}>
            <div className="relative">
              <img src={img.bible} alt="Open Bible" className="w-full object-cover" style={{ aspectRatio: "4/3" }} />
              <div className="absolute -bottom-6 -left-6 hidden max-w-xs bg-navy p-6 md:block">
                <p className="font-display text-sm uppercase tracking-[0.16em] text-brand-orange">Est. 2005</p>
                <p className="mt-2 text-sm text-white/80">Born as a prophetic declaration of mandate in the life of its founder.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MANDATE + SCRIPTURE */}
      <section className="relative overflow-hidden bg-navy py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2">
            <div>
              <span className="overline text-brand-orange">Our Mandate</span>
              <p className="mt-6 font-display text-3xl font-light leading-snug text-white md:text-4xl">
                “{m?.mandate}”
              </p>
              <div className="mt-8 flex items-center gap-3 text-white/60">
                <Flame className="h-5 w-5 text-brand-orange" />
                <span className="font-display text-lg italic">“{m?.mandate_imagery}”</span>
              </div>
            </div>
            <div className="border-l border-white/10 pl-8 lg:pl-16">
              <span className="overline text-brand-orange">Our Key Scripture</span>
              <Quote className="mt-6 h-10 w-10 text-brand-orange/40" />
              <p className="mt-4 font-display text-2xl font-light leading-relaxed text-white md:text-3xl">
                {m?.scripture_text}
              </p>
              <p className="mt-6 font-display text-sm font-semibold uppercase tracking-[0.2em] text-brand-orange">
                {m?.scripture_reference}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OUR EVANGELISM */}
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            overline="Our Evangelism"
            title="Seven ways we carry the Gospel to the nations"
            description="From mega city crusades to reaching one soul at a time — the harvest field is wide, and the mandate is clear."
          />
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {(activities || []).map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.04} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <ActivityCard activity={a} className="h-full" />
              </Reveal>
            ))}
          </div>
          <div className="mt-10">
            <Button asChild className="rounded-sm bg-navy font-display uppercase tracking-wide hover:bg-navy-surface">
              <Link to="/evangelism" data-testid="home-evangelism-link">See All Evangelism <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FOLLOW-UP & DISCIPLESHIP */}
      {m?.followup && (
        <section className="relative overflow-hidden bg-navy py-24 md:py-32">
          <div className="absolute inset-0 opacity-20">
            <img src={img.worship} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-navy/80" />
          </div>
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <SectionHeading dark overline="Follow-Up & Discipleship" title={m.followup.title} description={m.followup.intro} />
            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {m.followup.principles.map((p, i) => {
                const Icon = getIcon(p.icon);
                return (
                  <Reveal key={p.title} delay={i * 0.08}>
                    <div className="h-full border border-white/10 bg-white/5 p-8">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-sm bg-brand-orange text-white">
                        <Icon className="h-6 w-6" />
                      </span>
                      <h3 className="mt-6 font-display text-xl font-semibold text-white">{p.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-white/70">{p.description}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <p className="mt-12 font-display text-2xl font-light text-white md:text-3xl">
              “We count because <span className="text-brand-orange">every soul counts.</span>”
            </p>
          </div>
        </section>
      )}

      {/* TABERNACLE OF GRACE CHURCH */}
      <section className="bg-secondary/40 py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:items-center lg:gap-16 lg:px-8">
          <Reveal>
            <img src={church?.image || img.church} alt="Tabernacle of Grace Church" className="w-full object-cover" style={{ aspectRatio: "4/3" }} />
          </Reveal>
          <div>
            <span className="inline-flex items-center gap-2 overline text-brand-orange">
              <Church className="h-4 w-4" /> A Church Under The Ministry
            </span>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-navy md:text-5xl">
              Tabernacle of Grace Church
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              {church?.about}
            </p>
            <Button asChild className="mt-8 rounded-sm bg-brand-orange font-display uppercase tracking-wide hover:bg-brand-orange-600">
              <Link to="/tabernacle-of-grace" data-testid="home-church-link">Visit The Church <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* EVENTS + SERMONS + PODCASTS */}
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2">
            <div>
              <SectionHeading overline="Upcoming Events" title="Gather with us" />
              <div className="mt-8 space-y-4">
                {events && events.length > 0 ? (
                  events.slice(0, 3).map((e, i) => <EventCard key={i} event={e} />)
                ) : (
                  <EmptyState title="No upcoming events yet" description="Events will appear here as they are scheduled." />
                )}
              </div>
            </div>
            <div>
              <SectionHeading overline="Latest Sermons" title="Feed on the Word" />
              <div className="mt-8 grid gap-5 sm:grid-cols-2">
                {sermons && sermons.length > 0 ? (
                  sermons.slice(0, 2).map((s, i) => <SermonCard key={i} sermon={s} />)
                ) : (
                  <div className="sm:col-span-2">
                    <EmptyState title="Sermons coming soon" description="Recorded messages will be published here." />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-16">
            <SectionHeading overline="Latest Podcasts" title="Listen on the go" />
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {podcasts && podcasts.length > 0 ? (
                podcasts.slice(0, 3).map((p, i) => <PodcastCard key={i} podcast={p} />)
              ) : (
                <div className="md:col-span-3">
                  <EmptyState title="Podcasts coming soon" description="Episodes will be published here." />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT / MINISTRY */}
      <section className="bg-navy py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading dark align="center" overline="Our Reach" title="Taking the Gospel across the nations" description="Impact figures are published by the ministry as they become available." />
          {stats && stats.length > 0 ? (
            <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-4">
              {stats.map((s) => (
                <div key={s.id} className="bg-navy p-8 text-center">
                  <p className="font-display text-4xl font-extrabold text-brand-orange">{s.value}</p>
                  <p className="mt-2 text-xs uppercase tracking-[0.16em] text-white/50">{s.label}</p>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-4">
                {["Nations Reached", "Crusades Held", "Souls Counted", "Evangelists Sent"].map((label) => (
                  <div key={label} className="bg-navy p-8 text-center">
                    <p className="font-display text-4xl font-extrabold text-brand-orange">—</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.16em] text-white/50">{label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-center text-xs text-white/40">Placeholder figures — actual statistics to be provided.</p>
            </>
          )}
        </div>
      </section>

      {/* PRAYER + GIVING */}
      <section className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 md:grid-cols-2 lg:px-8">
          <div className="flex flex-col justify-between border border-border bg-secondary/40 p-10">
            <div>
              <HeartHandshake className="h-10 w-10 text-brand-orange" />
              <h3 className="mt-6 font-display text-3xl font-bold text-navy">Need Prayer?</h3>
              <p className="mt-3 text-muted-foreground">Submit a confidential prayer request. Our intercessory team stands with you.</p>
            </div>
            <Button asChild className="mt-8 w-fit rounded-sm bg-navy font-display uppercase tracking-wide hover:bg-navy-surface">
              <Link to="/prayer-requests" data-testid="home-prayer-link">Request Prayer <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="flex flex-col justify-between bg-navy p-10">
            <div>
              <Flame className="h-10 w-10 text-brand-orange" />
              <h3 className="mt-6 font-display text-3xl font-bold text-white">Support the Mission</h3>
              <p className="mt-3 text-white/70">Your partnership fuels crusades, outreach and discipleship across the nations.</p>
            </div>
            <Button asChild className="mt-8 w-fit rounded-sm bg-brand-orange font-display uppercase tracking-wide hover:bg-brand-orange-600">
              <Link to="/giving" data-testid="home-giving-link">Give / Partner <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
